<script setup lang="ts">
import { type SavedFolder } from "@gasparigit/omnia-sdk";
import type { GenericObject } from "vee-validate";
import { z } from "zod";
import { processError, requiredError, successToast } from "~/lib/utils/errors";
import type { ConfigItem } from "../ui/auto-form";

const omniaSdk = useOmniaSdk();

const opened = defineModel<boolean>("opened");
const props = defineProps<{
  parentId: number | null;
  savedFolder: SavedFolder | null;
}>();

const loading = ref<boolean>(false);
const emits = defineEmits(["success"]);

const formSchema = z.object({
  title: z.string(requiredError).describe("Titolo"),
  body: z.string().describe("Descrizione").optional(),
});

const form = useForm({ validationSchema: toTypedSchema(formSchema) });

watch(opened, () => {
  form.setValues({
    title: props.savedFolder?.title || "",
    body: props.savedFolder?.body || "",
  });
});

const fieldConfig: Record<string, ConfigItem> = {
  body: {
    component: "textarea",
  },
};

async function saveOrCreateFolder(values: GenericObject) {
  loading.value = true;

  try {
    if (props.savedFolder) {
      await omniaSdk.v1.savedFolders.update(props.savedFolder.id, { title: values["title"], body: values["body"] });
    } else {
      await omniaSdk.v1.savedFolders.create({ title: values["title"], body: values["body"], saved_folder_id: props.parentId });
    }

    successToast("Modifiche salvate con successo");
    emits("success");
  } catch (error) {
    processError(error);
  }

  loading.value = false;
}
</script>

<template>
  <Dialog v-model:open="opened">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogScrollContent>
      <DialogHeader>
        <DialogTitle>{{ savedFolder ? "Modifica" : "Nuova" }} cartella</DialogTitle>
      </DialogHeader>
      <AutoForm @submit="saveOrCreateFolder" :form="form" :schema="formSchema" :field-config="fieldConfig" class="content-card-grid grid gap-4">
        <DialogFooter>
          <DialogClose>
            <Button variant="ghost" type="reset">Annulla</Button>
          </DialogClose>
          <Button type="submit" :disabled="loading || !form.meta.value.valid" :class="{ 'animate-pulse': loading }">Salva</Button>
        </DialogFooter>
      </AutoForm>
    </DialogScrollContent>
  </Dialog>
</template>
